import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError } from '../../api/client'
import {
  COST_FORMATS,
  costReportQuery,
  downloadCostReport,
  type CostFormat,
  type CostReportPage as CostPage,
  type CostView,
} from '../../api/settingsCostReport'
import { shellQuery } from '../../api/shell'
import { AdmPageHead, type AdmStat } from '../../softsee/adm/AdmPageHead'
import { Forbidden } from '../../ui/Forbidden'
import { INNER_TABS_CLASS } from '../../ui/InnerTabs'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import { LoadFailed } from '../../ui/LoadFailed'
import { usePageStyles } from '../../ui/usePageStyles'
import { CompanyReportsFrame } from './CompanyReportsPage'

/**
 * Settings -> Reports -> Cost Reports: `settings/company_reports/cost_report`,
 * macheta `company_reports/cost/index.php`, element cu element: capul de
 * pagina al ADM-ului (`_pagehead`) cu totalurile pe moneda, bara From/To cu
 * lunile calculate, cele trei file (By service / By customer / By consumption)
 * si tabelele lor, cu aceleasi clase (`cr-*`, din `cost_report.css`).
 *
 * Fara total general, dinadins: fiecare suma sta pe moneda ei (vezi capul
 * machetei). Consumul se citeste numai pe o singura luna (From = To).
 */

const STYLES = ['css/adm.css', 'css/cost_report.css']

function money(value: number, currency: string): string {
  return `${value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} ${currency}`
}

function Unit({ value, currency }: { value: number | null; currency: string }) {
  return value === null ? <span className="cr-none">&mdash;</span> : <>{money(value, currency)}</>
}

export function CostReportPage() {
  const shell = useQuery(shellQuery('settings'))
  usePageStyles(STYLES, shell.data?.version ?? '')
  const [from, setFrom] = useState(0)
  const [to, setTo] = useState(0)
  const [view, setView] = useState<CostView>('service')
  const page = useQuery({ ...costReportQuery(from, to), placeholderData: keepPreviousData })

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <CompanyReportsFrame current="cost">
        <Forbidden />
      </CompanyReportsFrame>
    )
  }
  if (page.error) {
    return (
      <CompanyReportsFrame current="cost">
        <LoadFailed what="The cost report" error={page.error} onRetry={() => void page.refetch()} />
      </CompanyReportsFrame>
    )
  }
  if (!page.data) {
    return (
      <CompanyReportsFrame current="cost">
        <Spinner />
      </CompanyReportsFrame>
    )
  }

  const data: CostPage = page.data
  const { months, filters } = data
  const stats: AdmStat[] = Object.entries(data.data.totals).map(([currency, amount]) => ({
    value: `${Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} ${currency}`,
    label: 'costed',
    tone: 'blue-mauve',
  }))
  stats.push({ value: String(data.data.service.length), label: 'services', tone: 'blue-hoki' })
  stats.push({ value: String(data.data.customer.length), label: 'customers', tone: 'green-mode' })

  const exportAs = (which: CostView, format: CostFormat) => {
    void downloadCostReport(which, filters.from_ym, filters.to_ym, format).catch((err: unknown) =>
      toast(err instanceof ApiError && err.message ? err.message : 'The export could not be produced.', 'Export', 'error'),
    )
  }

  const tab = (id: CostView, label: string, n: number) => (
    <button type="button" className={'cr-tab' + (view === id ? ' is-on' : '')} data-view={id} role="tab" onClick={() => setView(id)}>
      {label} <span className="cr-tab-n">{n}</span>
    </button>
  )

  return (
    <CompanyReportsFrame current="cost">
      <div id="content" className="cr">
        <AdmPageHead tone="blue-mauve" icon="fa-coins" title="Cost Reports" note="what the services cost - by service, by customer, and by consumption" stats={stats} />
        {months.length === 0 ? (
          <div className="adm-card cr-empty">
            <p>
              <i className="fas fa-calculator"></i> No month has been costed yet, so there is nothing to aggregate.
            </p>
            <p className="cr-none">
              The costs are worked out from the service catalogue: <strong>Easeedesk &gt; Service Catalog</strong>, button{' '}
              <strong>Work out the costs</strong>. That is where the writing happens; this screen only reads.
            </p>
          </div>
        ) : (
          <>
            <form
              className="adm-bar cr-bar"
              onSubmit={(e) => {
                e.preventDefault()
              }}
            >
              <div className="adm-field">
                <label htmlFor="cost-from">From</label>
                <div>
                  <select id="cost-from" name="from_ym" className="form-control" value={filters.from_ym} onChange={(e) => setFrom(Number(e.target.value))}>
                    {months.map((m) => (
                      <option key={m.ym} value={m.ym}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="adm-field">
                <label htmlFor="cost-to">To</label>
                <div>
                  <select id="cost-to" name="to_ym" className="form-control" value={filters.to_ym} onChange={(e) => setTo(Number(e.target.value))}>
                    {months.map((m) => (
                      <option key={m.ym} value={m.ym}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <span className="cr-hint">
                <i className="far fa-building"></i> The customer is the one picked in the top bar.
              </span>
            </form>
            {/* `od-inner-tabs`: aceleasi file ca in Information si My Account (ui/InnerTabs.css). */}
            <div className={'line-tabs cr-tabs ' + INNER_TABS_CLASS} role="tablist">
              {tab('service', 'By service', data.data.service.length)}
              {tab('customer', 'By customer', data.data.customer.length)}
              {tab('consumption', 'By consumption', data.data.consumption.length)}
            </div>

            <div className="adm-card cr-panel" data-panel="service" hidden={view !== 'service'}>
              <div className="adm-card-head">
                <h5>What each service costs</h5>
                <span className="adm-card-note">the same catalogue entry costed for two customers is one service here, and says so</span>
                <ExportLinks view="service" onExport={exportAs} />
              </div>
              <table className="cr-table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Category</th>
                    <th>Kind</th>
                    <th className="cr-num">Customers</th>
                    <th className="cr-num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.service.map((row, i) => (
                    <tr key={i}>
                      <td className="cr-name">{row.service}</td>
                      <td>{row.category}</td>
                      <td>{row.kind}</td>
                      <td className="cr-num">{row.customers}</td>
                      <td className="cr-num cr-money">{money(row.amount, row.currency)}</td>
                    </tr>
                  ))}
                  {data.data.service.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="cr-none">
                        Nothing costed in this period.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            <div className="adm-card cr-panel" data-panel="customer" hidden={view !== 'customer'}>
              <div className="adm-card-head">
                <h5>What each customer is charged</h5>
                <span className="adm-card-note">showback and chargeback: one line per customer, per currency</span>
                <ExportLinks view="customer" onExport={exportAs} />
              </div>
              <table className="cr-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th className="cr-num">Services</th>
                    <th className="cr-num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.customer.map((row, i) => (
                    <tr key={i}>
                      <td className="cr-name">{row.customer}</td>
                      <td className="cr-num">{row.services}</td>
                      <td className="cr-num cr-money">{money(row.amount, row.currency)}</td>
                    </tr>
                  ))}
                  {data.data.customer.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="cr-none">
                        Nothing costed in this period.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            <div className="adm-card cr-panel" data-panel="consumption" hidden={view !== 'consumption'}>
              <div className="adm-card-head">
                <h5>What drove the cost</h5>
                <span className="adm-card-note">the config items, users and tickets the amount was worked out from - and the price of one</span>
                <ExportLinks view="consumption" onExport={exportAs} />
              </div>
              {!data.data.single ? (
                <p className="cr-note-warn">
                  <i className="fas fa-exclamation-triangle"></i> Consumption is read for one month at a time. Twelve monthly counts added together would look like a
                  count and be twelve times one. Pick the same month in <strong>From</strong> and <strong>To</strong>.
                </p>
              ) : (
                <>
                  <p className="cr-drivers">
                    <span>
                      <strong>{data.data.counts.items}</strong> config items
                    </span>
                    <span>
                      <strong>{data.data.counts.users}</strong> users
                    </span>
                    <span>
                      <strong>{data.data.counts.tickets}</strong> tickets
                    </span>
                  </p>
                  <table className="cr-table">
                    <thead>
                      <tr>
                        <th>Service</th>
                        <th>Customer</th>
                        <th className="cr-num">Items</th>
                        <th className="cr-num">Users</th>
                        <th className="cr-num">Tickets</th>
                        <th className="cr-num">Amount</th>
                        <th className="cr-num">Per item</th>
                        <th className="cr-num">Per user</th>
                        <th className="cr-num">Per ticket</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.data.consumption.map((row, i) => (
                        <tr key={i}>
                          <td className="cr-name">{row.service}</td>
                          <td>{row.customer}</td>
                          <td className="cr-num">{row.items}</td>
                          <td className="cr-num">{row.users}</td>
                          <td className="cr-num">{row.tickets}</td>
                          <td className="cr-num cr-money">{money(row.amount, row.currency)}</td>
                          <td className="cr-num">
                            <Unit value={row.per_item} currency={row.currency} />
                          </td>
                          <td className="cr-num">
                            <Unit value={row.per_user} currency={row.currency} />
                          </td>
                          <td className="cr-num">
                            <Unit value={row.per_ticket} currency={row.currency} />
                          </td>
                        </tr>
                      ))}
                      {data.data.consumption.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="cr-none">
                            Nothing costed in this month.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </CompanyReportsFrame>
  )
}

/* Aceleasi trei formate ca rapoartele dinamice: XLSX, CSV, PDF - aceleasi
   coloane, cerute de la acelasi capat (`export.{format}`). */
function ExportLinks({ view, onExport }: { view: CostView; onExport: (view: CostView, format: CostFormat) => void }) {
  return (
    <span className="cr-export">
      {COST_FORMATS.map((format) => (
        <a
          key={format}
          className="adm-card-link"
          href="#"
          data-export={view}
          data-format={format}
          onClick={(e) => {
            e.preventDefault()
            onExport(view, format)
          }}
        >
          Export {format.toUpperCase()}
        </a>
      ))}
    </span>
  )
}
